
import React, { useMemo } from 'react';
import { WorkItem, GeneratedImage } from '../types';
import { Eye, Wand2, Download, Search, LayoutGrid } from 'lucide-react';

interface GalleryViewProps {
  workItems: WorkItem[];
  onViewImage: (img: GeneratedImage) => void;
  onEditImage: (img: GeneratedImage) => void;
}

export const GalleryView: React.FC<GalleryViewProps> = ({ workItems, onViewImage, onEditImage }) => {
  // Flatten all images into a single list with parent info
  const allImages = useMemo(() => {
    const images: (GeneratedImage & { parentIndex: string })[] = [];
    workItems.forEach(item => {
      item.generatedImages.forEach(img => {
        images.push({ ...img, parentIndex: item.index });
      });
    });
    // Show newest first
    return images.sort((a, b) => b.timestamp - a.timestamp);
  }, [workItems]);

  if (allImages.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-slate-600 py-20">
        <div className="bg-slate-900/50 p-8 rounded-full mb-4 border border-slate-800">
            <LayoutGrid className="w-16 h-16 opacity-30" />
        </div>
        <p className="text-xl font-medium">暂无生成结果</p>
        <p className="text-sm mt-2">在列表中点击“批量生成”开始创作</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-bold text-white">所有生成结果</h2>
            <span className="bg-indigo-500/20 text-indigo-400 px-2 py-0.5 rounded text-xs font-mono">{allImages.length} 张图片</span>
          </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8 gap-4">
        {allImages.map((img) => (
          <div 
            key={img.id} 
            className="group relative aspect-square bg-slate-900 rounded-xl overflow-hidden border border-slate-800 hover:border-indigo-500 transition-all shadow-lg hover:shadow-indigo-500/10"
          >
            {/* Image */}
            <img 
                src={img.url} 
                alt="Generated asset" 
                className="w-full h-full object-cover cursor-pointer" 
                onClick={() => onViewImage(img)}
            />

            {/* Top Badge (Parent Index) */}
            <div className="absolute top-2 left-2 bg-black/60 backdrop-blur-md text-white text-[10px] font-mono px-2 py-0.5 rounded border border-white/10 opacity-80 group-hover:opacity-100 transition-opacity">
               #{img.parentIndex}
            </div>

            {/* Hover Actions */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3 gap-2">
               <p className="text-[10px] text-slate-300 line-clamp-2 leading-tight mb-1 select-none pointer-events-none">
                 {img.promptUsed}
               </p>
               <div className="flex gap-2">
                <button 
                    onClick={() => onViewImage(img)} 
                    className="flex-1 py-1.5 bg-slate-800 hover:bg-slate-700 text-white rounded-md text-[10px] font-bold flex items-center justify-center gap-1.5 transition-colors"
                >
                    <Eye size={12} /> 查看
                </button>
                <button 
                    onClick={() => onEditImage(img)} 
                    className="flex-1 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-md text-[10px] font-bold flex items-center justify-center gap-1.5 transition-colors shadow-lg shadow-indigo-900/40"
                >
                    <Wand2 size={12} /> 编辑
                </button>
               </div>
               <a 
                 href={img.url} 
                 download={`${img.parentIndex}_${img.id}.png`}
                 className="w-full py-1 text-center text-slate-400 hover:text-white text-[9px] uppercase tracking-wider font-bold"
               >
                 下载原图
               </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
